import React, { useLayoutEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/pro-regular-svg-icons/faCopy";
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { useUpdateElementData } from "../DocumentView/DocumentContext";

interface Props {
  element: DocumentElementType;
}

// Warm-dark code surface from the Modern Editor tokens.
const MONO = "font-jbmono";
const SURFACE = "rounded-[11px] bg-[#26241f] overflow-hidden";
const BODY = `w-full bg-transparent px-[16px] py-[15px] ${MONO} text-[13px] leading-[1.6] text-[#e6e3dc]`;

const CodeHeader: React.FC<{ getCode: () => string }> = ({ getCode }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center justify-between border-b border-[#383530] px-[14px] py-[9px]">
      <span className={`${MONO} text-[11.5px] font-semibold tracking-[0.02em] text-[#a8a49b]`}>
        Code
      </span>
      <button
        type="button"
        // Keep the editor selection alive when clicking the header.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          const text = getCode();
          if (navigator.clipboard) {
            navigator.clipboard.writeText(text).catch(() => {});
          }
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1300);
        }}
        className="inline-flex items-center gap-[5px] rounded-[6px] bg-[#353128] px-[9px] py-[4px] text-[11.5px] font-semibold text-[#c7c3ba] transition-colors hover:bg-[#403b30]"
      >
        <FontAwesomeIcon
          icon={copied ? faCheck : faCopy}
          className="h-[12px] w-[12px]"
        />
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

/**
 * A code block: a monospace, multiline editor on a warm-dark surface with a
 * header (label + copy). Not rich text and not mergeable — Enter inserts a
 * newline (native textarea). Content is stored as plain text in `data.content`.
 * Read-only outside the editor renders a <pre>.
 *
 * The textarea is deliberately UNCONTROLLED — writing each keystroke back
 * through the deep immer document update and re-seeding `value` can drop fast
 * keystrokes; we seed on mount and only re-sync from props when unfocused.
 */
const PageCode: React.FC<Props> = ({ element }) => {
  const data = element.data as { content?: string };
  const code = typeof data?.content === "string" ? data.content : "";
  const update = useUpdateElementData(element.id);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const grow = () => {
    const node = ref.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${node.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (document.activeElement !== node && node.value !== code) {
      node.value = code;
    }
    grow();
  }, [code]);

  if (!update) {
    return (
      <div className={SURFACE}>
        <CodeHeader getCode={() => code} />
        <pre className={`${BODY} overflow-x-auto`}>
          <code>{code}</code>
        </pre>
      </div>
    );
  }

  return (
    <div className={SURFACE}>
      <CodeHeader getCode={() => ref.current?.value ?? code} />
      <textarea
        ref={ref}
        defaultValue={code}
        spellCheck={false}
        rows={1}
        placeholder="Enter code…"
        onInput={(e) => {
          const value = e.currentTarget.value;
          grow();
          update((d) => {
            d.content = value;
          });
        }}
        className={`${BODY} resize-none overflow-hidden outline-none placeholder:text-[#6b7684]`}
      />
    </div>
  );
};

export default PageCode;
